/**
 * EmployeeWageMappingTable — thin rendering shell.
 *
 * All state, data fetching, and business logic live in:
 *   src/Components/WageMapping/useWageMappingTable.js
 *
 * Sub-components:
 *   WageRow              — a single memoized wage row
 *   WageMappingTableHead — column headers + add button
 *   WageMappingTableFooter — total gross pay display
 */
import React, { useMemo, useCallback } from "react";

import { Box, Table, TableBody, TableContainer } from "@mui/material";
import { FieldArray } from "formik";
import PayrollsPopup from "./PayrollPopup";
import AddCOA from "../QuickAdd/AddCOA";

import {
    useWageMappingTable,
    WageRow,
    WageMappingTableHead,
    WageMappingTableFooter,
} from "./WageMapping";

const EmployeeWageMappingTable = ({
    row,
    wageItems,
    setLoadingDetails,
    accountOptions,
    formValues,
    corporationId,
    onTotalGrossChange,
    onAddNewMappedItem,
    refreshTrigger,
    manualDeptJobChange,
    isReadOnly,
    skipPayrollDetailsLoad,
    isReportScreen: propIsReportScreen,
    isCompletedRecord: propIsCompletedRecord,
    isNewEmployeeJustAdded,
    setInitialState,
}) => {
    const {
        values,
        setFieldValue,
        errors,
        touched,
        enqueueSnackbar,
        removingIndex,
        setRemovingIndex,
        alertPopup,
        showDeleteConfirm,
        hidePopup,
        openAddCOA,
        setOpenAddCOA,
        pendingCOAField,
        setPendingCOAField,
        isType1,
        showItemColumn,
        wageOptions,
        normalizedOptionsMap,
        enhancedAccountOptions,
        accountOptionsMap,
        totalGross,
        handleCOA,
        refreshAccounts,
    } = useWageMappingTable({
        row, formValues, corporationId, accountOptions, wageItems,
        onTotalGrossChange, setLoadingDetails, setInitialState,
        refreshTrigger, manualDeptJobChange, isReadOnly,
        skipPayrollDetailsLoad,
        isReportScreen: propIsReportScreen,
        isCompletedRecord: propIsCompletedRecord,
        isNewEmployeeJustAdded,
    });

    // ─── Grouped context (passed as single 'ctx' prop) ────────────────────
    const wageCtx = useMemo(() => ({
        showItemColumn,
        isReadOnly,
        isHoursDecimal: values.IsHoursDecimal,
        isType1,
        formValuesDepartment: formValues?.department,
        formValuesJob: formValues?.job,
        normalizedOptions: wageOptions,
        normalizedOptionsMap,
        accountOptionsMap,
        accountOptions: enhancedAccountOptions,
    }), [
        showItemColumn,
        isReadOnly,
        values.IsHoursDecimal,
        isType1,
        formValues?.department,
        formValues?.job,
        wageOptions,
        normalizedOptionsMap,
        accountOptionsMap,
        enhancedAccountOptions,
    ]);

    // ─── Grouped handlers builder (passed as single 'handlers' prop) ──────
    const getWageHandlers = useCallback((remove) => ({
        setFieldValue,
        enqueueSnackbar,
        handleCOA,
        onAddNewMappedItem,
        remove,
        setRemovingIndex,
        showDeleteConfirm,
        setOpenAddCOA,
        setPendingCOAField,
    }), [
        setFieldValue,
        enqueueSnackbar,
        handleCOA,
        onAddNewMappedItem,
        setRemovingIndex,
        showDeleteConfirm,
        setOpenAddCOA,
        setPendingCOAField,
    ]);

    return (
        <>
            <FieldArray name="wage">
                {({ push, remove }) => (
                    <Box>
                        <TableContainer sx={{ backgroundColor: "fff", borderRadius: 2, overflowX: "auto", border: "1px solid #E5E7EB" }}>
                            <Table size="small" sx={{ minWidth: 750, "& .MuiTableCell-root": { borderBottom: "1px solid #f2f2f2" } }}>
                                <WageMappingTableHead
                                    showItemColumn={showItemColumn}
                                    isReadOnly={isReadOnly}
                                    isHoursDecimal={values.IsHoursDecimal}
                                    onAddRow={() =>
                                        push({
                                            id: Date.now(),
                                            wageName: "",
                                            mappedTo: "",
                                            coa: "",
                                            hrs: values.IsHoursDecimal ? "0.00" : "0:00",
                                            rate: "0.00",
                                            gross: "0.00",
                                        })
                                    }
                                />
                                <TableBody>
                                    {values.wage.map((wageRow, index) => (
                                        <WageRow
                                            key={wageRow.id || index}
                                            rowData={{
                                                row: wageRow,
                                                index,
                                                isRemoving: removingIndex === index,
                                                touchedRow: touched?.wage?.[index],
                                                errorRow: errors?.wage?.[index],
                                            }}
                                            ctx={wageCtx}
                                            handlers={getWageHandlers(remove)}
                                        />
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>


                        <WageMappingTableFooter totalGross={totalGross} />
                    </Box>
                )}
            </FieldArray>

            <PayrollsPopup
                open={alertPopup.open}
                popupType={alertPopup.type}
                message={alertPopup.message}
                onConfirm={alertPopup.onConfirm}
                onCancel={hidePopup}
            />

            <AddCOA
                open={openAddCOA}
                corporationId={corporationId || values?.corporationId || values?.corpId || ""}
                corporationName={values?.corporation?.corpName || values?.corporation || ""}
                onClose={() => {
                    setOpenAddCOA(false);
                    setPendingCOAField("");
                }}
                setFieldValue={setFieldValue}
                pendingCOAField={pendingCOAField}
                onSuccess={async () => {
                    await refreshAccounts();
                }}
            />
        </>
    );
};

export default EmployeeWageMappingTable;